export default class Configuration {
    constructor(options = {}) {
        this.options = {
            configurationSelector: '.configuration',
            roomTabSelector: '.configuration__room-tab-input',
            technologyToggleSelector: '.configuration__technology-toggle-input',
            ...options
        };

        this.configurationElement = null;
        this.roomTabInputs = [];
        this.technologyToggleInputs = [];
        this.eventHandlers = [];
        
        this.init();
    }

    init() {
        // Find the configuration section element
        this.configurationElement = document.querySelector(this.options.configurationSelector);
        
        if (!this.configurationElement) {
            console.warn('Configuration element not found');
            return;
        }

        // Find all room tab radio inputs
        this.roomTabInputs = Array.from(
            document.querySelectorAll(this.options.roomTabSelector)
        );

        // Find all technology toggle radio inputs
        this.technologyToggleInputs = Array.from(
            document.querySelectorAll(this.options.technologyToggleSelector)
        );

        if (this.roomTabInputs.length === 0 || this.technologyToggleInputs.length === 0) {
            console.warn('Room tabs or technology toggle inputs not found');
            return;
        }

        // Set up event listeners for room tab changes
        this.roomTabInputs.forEach(input => {
            const handler = () => this.updateBackgroundImage();
            input.addEventListener('change', handler);
            this.eventHandlers.push({ element: input, event: 'change', handler });
        });

        // Set up event listeners for technology toggle changes
        this.technologyToggleInputs.forEach(input => {
            const handler = () => this.updateBackgroundImage();
            input.addEventListener('change', handler);
            this.eventHandlers.push({ element: input, event: 'change', handler });
        });

        // Preload all configuration images
        this.preloadImages();

        // Update background image on initial load to match default selections
        this.updateBackgroundImage();
    }

    preloadImages() {
        if (this.roomTabInputs.length === 0) {
            return;
        }

        // Collect all unique image URLs from room tab inputs
        const imageUrls = new Set();
        
        this.roomTabInputs.forEach(input => {
            const teamsImage = input.getAttribute('data-room-teams');
            const zoomImage = input.getAttribute('data-room-zoom');
            
            if (teamsImage) {
                imageUrls.add(teamsImage);
            }
            if (zoomImage) {
                imageUrls.add(zoomImage);
            }
        });

        // Preload each image
        imageUrls.forEach(url => {
            const img = new Image();
            img.src = url;
        });
    }

    updateBackgroundImage() {
        if (!this.configurationElement) return;

        // Get the selected room tab radio button
        const selectedRoomTab = this.roomTabInputs.find(input => input.checked);
        
        if (!selectedRoomTab) {
            console.warn('No room tab selected');
            return;
        }

        // Get the selected technology toggle radio button
        const selectedTechnologyToggle = this.technologyToggleInputs.find(input => input.checked);
        
        if (!selectedTechnologyToggle) {
            console.warn('No technology toggle selected');
            return;
        }

        // Determine which data attribute to read based on technology value
        const technologyValue = selectedTechnologyToggle.value;
        const dataAttribute = technologyValue === 'microsoft' 
            ? 'data-room-teams' 
            : 'data-room-zoom';

        // Get the image URL from the selected room tab's data attribute
        const imageUrl = selectedRoomTab.getAttribute(dataAttribute);
        
        if (!imageUrl) {
            console.warn(`No image URL found in ${dataAttribute} for selected room tab`);
            return;
        }

        // Update the background image style
        this.configurationElement.style.backgroundImage = `url('${imageUrl}')`;

        // Announce the change to screen readers
        const announcementElement = document.getElementById('configuration-announcement');
        if (announcementElement) {
            const roomLabel = selectedRoomTab.getAttribute('aria-label') || selectedRoomTab.value;
            const technologyLabel = selectedTechnologyToggle.getAttribute('aria-label') || selectedTechnologyToggle.value;
            announcementElement.textContent = `Room configuration updated to ${roomLabel} with ${technologyLabel}`;
        }
    }

    destroy() {
        // Remove all event listeners
        this.eventHandlers.forEach(({ element, event, handler }) => {
            element.removeEventListener(event, handler);
        });

        // Clear references
        this.configurationElement = null;
        this.roomTabInputs = [];
        this.technologyToggleInputs = [];
        this.eventHandlers = [];
    }
}
